import Image from "../gui/Image";
import View from "../gui/View";
import Modal from "../gui/Modal";
import AbstractProvidedClass from "./AbstractProvidedClass";
import EventTypes from "./EventTypes";
import { migrate } from "./DataMigrations/DataMigrator";

export default class AbstractDataHandler extends AbstractProvidedClass {
    get csrfToken() {
        return this.p.select('meta[name="csrf-token"]')?.elt?.content || '';
    }

    executeGetRequest(uri, func) {
        // Native fetch (not p5.httpDo): httpDo's error callback assumes a JSON
        // body and runs JSON.parse on it, which itself throws on a network-level
        // failure ("Failed to fetch") that has no body. That uncaught rejection
        // bubbled through setup and left the planner stuck behind a fullscreen
        // spinner with no way to recover. fetch lets us catch the failure and
        // offer a retry, mirroring executeSaveRequest.
        fetch(uri, {
            method: 'GET',
            headers: {
                'X-CSRF-Token': this.csrfToken,
                'Accept': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Plan data request failed: ${response.status}`);
                }
                return response.json();
            })
            .then(
                (data) => {
                    // Run the caller's handler outside the fetch promise chain
                    // (via the onFulfilled arg, not a trailing .then) so a genuine
                    // data/render error isn't swallowed and misreported as a
                    // network failure — only the fetch/parse rejects land below.
                    func(data);
                    this.spinner.reset();
                },
                () => this.handleLoadError(uri, func)
            );
    }

    handleLoadError(uri, func) {
        // The plan data couldn't be fetched (transient network failure, non-2xx,
        // or malformed body). Reset the spinner so the planner isn't left stuck,
        // then offer a retry instead of failing silently.
        this.spinner?.reset();
        window.posthog?.captureException?.(new Error('Plan data load failed'));
        this.showLoadErrorModal(uri, func);
    }

    showLoadErrorModal(uri, func) {
        const modal = new Modal(this.provider);
        modal
            .setTitle("Couldn't load your plan")
            .addContent(this.p.createElement('p', "We couldn't reach the server. Check your connection and try again."))
            .setButtonText('Retry')
            .onClickButton(() => {
                modal.remove();
                this.spinner.showFullscreen();
                this.executeGetRequest(uri, func);
            });
    }

    executeSaveRequest(uri, method, body, func) {
        window['saveButton'].html('...');
        // Native fetch (not p5.httpDo): httpDo throws away the response body on a
        // non-2xx, so we couldn't tell the user *why* a save failed. fetch lets us
        // read the server's validation errors.
        fetch(uri, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': this.csrfToken,
                'Accept': 'application/json',
            },
            body: JSON.stringify(body),
        }).then((response) => {
            if (!response.ok) {
                return response.json()
                    .catch(() => null)
                    .then((errors) => this.handleSaveError(this.parseSaveError(errors)));
            }
            return response.json().then((data) => {
                this.storage.empty();
                this.changesSaved();
                func(data);
            });
        }).catch(() => {
            // Network/connection failure — no response body to read.
            this.handleSaveError(this.parseSaveError(null));
        });
    }

    handleSaveError(message) {
        // Clear the spinner and restore the save button to its unsaved (red)
        // state so the user can retry, then tell them what went wrong — without
        // this the save silently fails and the button stays stuck on "...".
        this.spinner?.reset();
        this.provider.saveFailed?.();
        this.showSaveErrorModal(message);
        window.posthog?.captureException?.(new Error(`Plan save failed: ${message}`));
    }

    parseSaveError(errors) {
        // Rails renders validation failures as { field: [messages] }.
        if (errors && typeof errors === 'object') {
            const messages = Object.entries(errors).map(([field, msgs]) => {
                const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
                return field === 'base' ? text : `${field} ${text}`;
            });
            if (messages.length) return messages.join('. ');
        }
        return 'Something went wrong while saving. Please try again.';
    }

    showSaveErrorModal(message) {
        const modal = new Modal(this.provider);
        modal
            .setTitle("Couldn't save your plan")
            .addContent(this.p.createElement('p', message))
            .setButtonText('OK')
            .onClickButton(() => modal.remove());
        modal.onClickOutside(() => modal.remove());
    }

    loadData(data, storageData = null) {
        if (storageData && storageData.timestamp >= data.timestamp) {
            this.changesMade(true);
        }
        let newData = migrate(data, storageData);
        this.speciesCollection.init(newData.species);
        this.layers.init(newData.layers);
        this.view.init(newData.view);
        this.settings.init(newData.settings);
        this.elements.init(newData.elements);
        // No usable saved view (e.g. {scale:null}) — frame the plan instead of
        // rendering at a NaN/garbage transform.
        if (!View.isValidView(newData.view) && this.elements.length > 0) {
            this.view.reset();
        }
        if (newData.image) {
            this.imageMenu?.loading();
            this.image = new Image(this.provider, newData.image);
        }
        this.planName = newData.name;
        this.grid.recalculate();
        this.loaded = true;
        this.emit(EventTypes.DATA_LOADED);
        return newData;
    }
}
